import api from '../services/api';
import toast from './toast';

// Check if Checklist is required for a specific document type
export const isVerificationRequired = async (documentType) => {
  try {
    const response = await api.get('/invoices/admin/settings');
    const settings = response.data;
    
    if (!settings.documentVerification?.enabled) {
      return false;
    }
    
    return settings.documentVerification.requireVerificationFor[documentType] || false;
  } catch (error) {
    console.error('Error checking verification requirement:', error);
    return false;
  }
};

// Create a verification record for an uploaded document
export const createVerificationRecord = async (documentData) => {
  try {
    
    const response = await api.post('/checklist/create', documentData);
    
    if (response.data.verificationRequired) {
      toast.success('Document uploaded successfully. Verification required before approval.');
      return {
        verificationId: response.data.verificationId,
        verificationRequired: true
      };
    } else {
      toast.success('Document uploaded successfully.');
      return {
        verificationRequired: false
      };
    }
  } catch (error) {
    console.error('Error creating verification record:', error);
    toast.error('Failed to process Checklist');
    throw error;
  }
};

// Get verification status for a document
export const getVerificationStatus = async (documentType, documentId, documentField) => {
  try {
    const response = await api.get('/checklist/verifiable');
    const verifications = response.data.data || [];
    
    const verification = verifications.find(v => 
      v.documentType === documentType && 
      v.documentId === documentId && 
      v.documentField === documentField
    );
    
    return verification ? verification.status : null;
  } catch (error) {
    console.error('Error getting verification status:', error);
    return null;
  }
};

// Get verifications for a specific form submission
export const getVerificationsForFormSubmission = async (formSubmissionId) => {
  try {
    const response = await api.get(`/checklist/form-submission/${formSubmissionId}`);
    return response.data.data || [];
  } catch (error) {
    console.error('Error getting verifications for form submission:', error);
    return [];
  }
};

// Get verifications for a specific task
export const getVerificationsForTask = async (taskId) => {
  try {
    const response = await api.get(`/checklist/task/${taskId}`);
    return response.data.data || [];
  } catch (error) {
    console.error('Error getting verifications for task:', error);
    return [];
  }
};

// Get status badge component
export const getStatusBadge = (status) => {
  const statusConfig = {
    pending: { 
      color: 'bg-yellow-100 text-yellow-800', 
      text: 'PENDING',
      icon: '⏳'
    },
    approved: { 
      color: 'bg-green-100 text-green-800', 
      text: 'APPROVED',
      icon: '✅'
    },
    rejected: { 
      color: 'bg-red-100 text-red-800', 
      text: 'REJECTED',
      icon: '❌'
    },
    auto_approved: { 
      color: 'bg-blue-100 text-blue-800', 
      text: 'AUTO-APPROVED',
      icon: '✅'
    }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return {
    className: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`,
    text: config.text,
    icon: config.icon
  };
};

// Format file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Format date
export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Get document type label
export const getDocumentTypeLabel = (documentType) => {
  const typeLabels = {
    formSubmission: 'Form Submission',
    masterData: 'Master Data',
    productTemplate: 'Product Template',
    taskAttachment: 'Task Attachment'
  };
  return typeLabels[documentType] || documentType;
}; 